/**
 * URL-only avatar resolver for the Pixi wheel-resolve scene. Producer
 * brief 2026-05-16 — the React {@code SeatAvatar} component owned
 * the React-side avatar mapping (BOT → archetype PNG, PLAYER →
 * chosen default). The Pixi scene needs the raw URLs so it can
 * load them via {@code Assets.load} and bind to Sprites. This
 * module centralises that resolution so the two stay consistent.
 *
 * <p>Stage 3 install-excision (2026-06-06) — the 8 archetype idle
 * URLs used to be bundled via static imports (~24MB into the install).
 * They're now resolved through {@link resolveEntryUrl} so production
 * builds fetch from CloudFront via the asset manifest.
 */
import type { WheelSeatView } from '@goodtime/shared';
import { resolveEntryUrl } from '@goodtime/engine';
import { getDefaultAvatarUrl } from '../../avatar/defaultAvatars';

/** Archetype enum value → repo-relative path under apps/mobile/src/assets/.
 *  Matches the AssetManifestService's key format so {@link resolveEntryUrl}
 *  can route through CDN. */
const ARCHETYPE_PATH: Record<string, string> = {
  BRAVE_BEA: 'avatar/brave_bea/idle.png',
  CALM_CARL: 'avatar/calm_carl/idle.png',
  FRIENDLY_FOX: 'avatar/friendly_fox/idle.png',
  LUCKY_LOU: 'avatar/lucky_lou/idle.png',
  QUIET_QUINN: 'avatar/quiet_quinn/idle.png',
  SHIELDED_SAM: 'avatar/shielded_sam/idle.png',
  STOIC_STAN: 'avatar/stoic_stan/idle.png',
  WILD_WILLA: 'avatar/wild_willa/idle.png',
};

export function resolveAvatarUrl(
  seat: WheelSeatView,
  localPlayerAvatarId: string | null,
): string | null {
  if (seat.kind === 'BOT' && seat.archetype) {
    const path = ARCHETYPE_PATH[seat.archetype];
    if (!path) return null;
    return resolveEntryUrl({ path });
  }
  if (seat.kind === 'PLAYER') {
    return getDefaultAvatarUrl(localPlayerAvatarId, 'idle');
  }
  return null;
}
